const SCROLL_LEFT_SWATCH_QUERY_PARAM = 'sl';

class ProductSwatches extends HTMLElement {
  constructor() {
    super();

    // Breakpoints: 1024 (64em), 575, 375. Max visible: 4 → 3 → 2 → 1. Scroll by: 2 (≥576px), 1 (≤575px).
    // At ≤375px with only 2 swatches we use centered layout (no carousel) via applySwatchOverflow.
    this.DESKTOP_BREAKPOINT_PX = 1024; // 64em
    this.BREAKPOINT_575_PX = 575;
    this.BREAKPOINT_375_PX = 375;
    this.MAX_VISIBLE_DESKTOP = 4;
    this.MAX_VISIBLE_BELOW = 3;
    this.MAX_VISIBLE_375 = 1;
    this.SCROLL_SWATCHES_BY = 2;

    // State
    this.parent = this.closest('.product-card');
    this.primaryImage = this.parent.querySelector('.product-card__primary .rsp-Image img');
    this.secondaryImage = this.parent.querySelector('.product-card__secondary .rsp-Image img');
    this.productTitles = this.parent.querySelectorAll('.product-card-title');

    // Bindings
    this.onSwatchClick = this.onSwatchClick.bind(this);
  }

  // Lifecycle
  connectedCallback() {
    this.applySwatchOverflow();

    // Ensure active swatch is visible on load (no animation)
    this.querySelectorAll('.product__variant-options')
      .forEach(fieldset => {
        if (fieldset.classList.contains('is-scrollable')) {
          this.scrollActiveSwatchIntoView(fieldset, 'auto');
        }
    });
    this.attachEvents();
    this.attachDragScroll();
    this.initSwatchNavButtons();
  }

  disconnectedCallback() {
    this.detachEvents();
  }

  // Events
  attachEvents() {
    this.addEventListener("click", this.onSwatchClick);
    this.addEventListener("mouseover", (e) => {
      const swatch = e.target.closest("label");
      if (swatch && !this.shouldHideTooltip) {
        e.preventDefault();
        e.stopPropagation();
        this.showTooltip(swatch);
      }
    });

    this.addEventListener("mouseout", (e) => {
      const swatch = e.target.closest("label");
      if (swatch) this.hideTooltip();
    });

    // Keyboard accessibility
    this.addEventListener("focusin", (e) => {
      const swatch = e.target.closest("label");
      if (swatch && !this.shouldHideTooltip) {
        e.preventDefault();
        e.stopPropagation();
        this.showTooltip(swatch);
      }
    });

    this.addEventListener("focusout", () => {
      this.hideTooltip();
    });
    this._onResize = () => {
      this.applySwatchOverflow();
    };
    window.addEventListener('resize', this._onResize);
    this.observeResize();  
    this.addEventListener('swatch:sync', this.onSwatchSync.bind(this));
    this.parent.addEventListener('click', (e) => {
      window.location = this.parent.dataset.href;
    })
  }

  getMaxVisible() {
    const w = window.innerWidth;
    if (w >= this.DESKTOP_BREAKPOINT_PX) return this.MAX_VISIBLE_DESKTOP;
    if (w > this.BREAKPOINT_575_PX) return this.MAX_VISIBLE_BELOW;
    if (w > this.BREAKPOINT_375_PX) return 2;
    return this.MAX_VISIBLE_375;
  }

  getScrollBy() {
    const w = window.innerWidth;
    return w <= this.BREAKPOINT_575_PX ? 1 : this.SCROLL_SWATCHES_BY;
  }

  /** True when tooltips should be hidden (set data-hide-tooltip on the element). */
  get shouldHideTooltip() {
    return this.hasAttribute('data-hide-tooltip');
  }


  observeResize() {
    this._ro = new ResizeObserver(() => {
      this.applySwatchOverflow();
    });

    this._ro.observe(this);
  }

  detachEvents() {
    this.removeEventListener("click", this.onSwatchClick);
  }

  attachDragScroll() {
    this.querySelectorAll('.product__variant-options.is-scrollable').forEach(container => {
      let isDown = false;
      let startX;
      let scrollLeft;
      const flickityInstance =
      container.closest('.flickity-enabled')?.flickity;

      container.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        isDown = true;
        container.classList.add('active');
        startX = e.pageX - container.offsetLeft;
        scrollLeft = container.scrollLeft;
        if (flickityInstance) {
          flickityInstance.options.draggable = false;
        }
      });

      container.addEventListener('mouseleave', (e) => {
        e.preventDefault();
        e.stopPropagation();
        isDown = false;
        container.classList.remove('active');
         if (flickityInstance) {
          flickityInstance.options.draggable = true;
        }
      });

      container.addEventListener('mouseup', (e) => {
        e.preventDefault();
        e.stopPropagation();
        isDown = false;
        container.classList.remove('active');
      });

      container.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        e.stopPropagation();
        const x = e.pageX - container.offsetLeft;
        const walk = (x - startX) * 1.5; // scroll speed multiplier
        container.scrollLeft = scrollLeft - walk;
      });

      container.addEventListener('touchstart', (e) => {
        e.stopPropagation();
        if (flickityInstance) {
          flickityInstance.options.draggable = false;
        }
      })

      container.addEventListener('touchend', (e) => {
        e.stopPropagation();
        if (flickityInstance) {
          flickityInstance.options.draggable = true;
        }
      })
    });
  }

  onSwatchClick(event) {
    const swatch = event.target.closest("label");
    if (!swatch) return;

    event.preventDefault();
    event.stopPropagation();

    const url = swatch.dataset.link;
    const primaryImageSrc = swatch.dataset.imagePrimary;
    const secondaryImageSrc = swatch.dataset.imageSecondary;
    const title = swatch.dataset.title;

    // Build new srcset with different widths
    const widths = [180, 360, 540, 720, 900];
    const srcsetValuesPrimary = widths.map(width => 
      `${primaryImageSrc}&width=${width} ${width}w`
    ).join(', ');
    const srcsetValuesSecondary = widths.map(width => 
      `${secondaryImageSrc}&width=${width} ${width}w`
    ).join(', ');

    // update card details on swatch click
    this.parent.dataset.href = url;
    this.primaryImage.src = primaryImageSrc;
    this.primaryImage.srcset = srcsetValuesPrimary;
    this.secondaryImage.src = secondaryImageSrc;
    this.secondaryImage.srcset = srcsetValuesSecondary;

    const displayTitle = title ? title.split(' - ')[0].trim() : title;
    this.productTitles.forEach(titleEl => {
      titleEl.textContent = displayTitle;
    });

    // Update price when swatch (variant) changes
    const priceText = swatch.dataset.productPrice;
    const priceRaw = swatch.dataset.price;
    if (priceText != null && this.parent) {
      this.parent.querySelectorAll('.product-price [js-currency="price"]').forEach(span => {
        if (span.closest('s') == null) {
          span.textContent = priceText;
          if (priceRaw != null) span.setAttribute('data-price', priceRaw);
        }
      });
    }

    this.setActiveSwatch(swatch);
    this.dispatchSwatchSync(swatch);

    const fieldset = swatch.closest('.product__variant-options');

    if (fieldset?.classList.contains('is-scrollable')) {
      this.scrollActiveSwatchIntoView(fieldset);
    }
  }

  dispatchSwatchSync(swatch) {
    this.dispatchEvent(
      new CustomEvent('swatch:sync', {
        bubbles: true,
        detail: {
          productId: this.dataset.productId,
          swatchId: swatch.dataset.swatchId 
        }
      })
    );
  }

  setActiveSwatch(swatch) {
    swatch
      .closest('.product__variant-options')
      ?.querySelectorAll('.product-option__label')
      .forEach(el => el.classList.remove('active'));

    swatch.classList.add('active');
  }

  // There are two instances of swatches carousels per product card.
  // One initial layer and one on hover, so this function keeps
  // the scroll position in sync for both.
  onSwatchSync(event) {
    const { productId, swatchId } = event.detail;

    // Ignore if not the same product
    if (productId !== this.dataset.productId) return;

    const targetSwatches = this.parent.querySelectorAll(
    `label[data-swatch-id="${swatchId}"]`
    );

    // Ignore origin or missing
    targetSwatches.forEach(target => {
      if (!target || target.classList.contains('active')) return;
      const fieldset = target.closest('.product__variant-options');
      this.setActiveSwatch(target);
      if (fieldset.classList.contains('is-scrollable')) {
        this.scrollActiveSwatchIntoView(fieldset);
      }
    });
  }

  // Swatch overflow helper (responsive: tablet ≤768px shows 3, desktop shows 4).
  // At ≤375px with exactly 2 swatches use centered layout (no carousel).
  applySwatchOverflow() {
    const w = window.innerWidth;
    let maxVisible = this.getMaxVisible();
    this.querySelectorAll('.product__variant-options').forEach(fieldset => {
      const swatches = fieldset.querySelectorAll('.product-option__label');
      const swatchCount = parseInt(fieldset.dataset.swatchCount) || swatches.length;
      // At ≤375px with exactly 2 swatches, force centered layout (no carousel)
      const effectiveMax = (w <= this.BREAKPOINT_375_PX && swatchCount === 2) ? 2 : maxVisible;

      if (swatches.length > effectiveMax) {
        fieldset.classList.add('is-scrollable');
        fieldset.classList.remove('product__variant-options--centered');
        fieldset.dataset.peek = 'true';
        fieldset.setAttribute('tabindex', '0');
      } else {
        fieldset.classList.remove('is-scrollable');
        fieldset.classList.add('product__variant-options--centered');
        fieldset.removeAttribute('data-peek');
        fieldset.removeAttribute('tabindex');
      }
    });
    
    // Update nav button visibility after changing layout
    this.updateSwatchNavState();
  }

 
  scrollActiveSwatchIntoView(fieldset, behavior = 'smooth') {
    const activeSwatch = fieldset.querySelector(
      '.product-option__label.active'
    );

    if (!activeSwatch) return;

    const fieldsetRect = fieldset.getBoundingClientRect();
    const swatchRect = activeSwatch.getBoundingClientRect();
    const scrollLeft = fieldset.scrollLeft;
    // Center the swatch horizontally within the fieldset's visible area
    const swatchCenter = swatchRect.left - fieldsetRect.left + swatchRect.width / 2 + scrollLeft;
    const targetScroll = swatchCenter - fieldsetRect.width / 2;
    const clampedScroll = Math.max(0, Math.min(targetScroll, fieldset.scrollWidth - fieldset.clientWidth));

    fieldset.scrollTo({
      left: clampedScroll,
      behavior
    });
  }

  getSiblingSwatchContainer() {
    const card = this.closest('.product-card');
    if (!card) return null;

    return [...card.querySelectorAll('product-swatches')]
      .find(el => el !== this);
  }

  initSwatchNavButtons() {
    const container = this.querySelector('.product__variant-options');
    if (!container) return;

    // Always set so sibling scroll sync works when the other instance has the buttons
    this.swatchScrollContainer = container;

    // Add arrows when card can ever be scrollable (2+ swatches); visibility updated on resize by updateSwatchNavState
    if (container.children.length <= 1) return;

    // Already have buttons (e.g. init ran twice on same element)
    if (this.querySelector('.swatch-nav--prev')) return;

    /* --- create buttons --- */
    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'swatch-nav swatch-nav--prev';
    prevBtn.setAttribute('aria-label', 'Scroll swatches left');
    prevBtn.innerHTML = `
                          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                            <path d="M10 3L5 8L10 13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                          `;
    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'swatch-nav swatch-nav--next';
    nextBtn.setAttribute('aria-label', 'Scroll swatches right');
    nextBtn.innerHTML = `
                        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M6 3L11 8L6 13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        `;

    this.insertBefore(prevBtn, this.firstChild);
    this.appendChild(nextBtn);

    /* --- click handlers --- */
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.scrollSwatchesBy(-1 * this.getScrollBy());
    });
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.scrollSwatchesBy(this.getScrollBy());
    });

    /* --- update visibility --- */
    const update = () => this.updateSwatchNavState();
    container.addEventListener('scroll', update);
    window.addEventListener('resize', update);

    this.updateSwatchNavState();
  }

  scrollSwatchesBy(direction) {
    const container = this.swatchScrollContainer;
    if (!container) return;

    const swatch = container.querySelector('.product-option__label');
    if (!swatch) return;

    const gap = parseFloat(getComputedStyle(container).columnGap || 0);
    const step = swatch.offsetWidth + gap;
    const delta = step * direction;

    /* --- scroll current --- */
    this.animateScrollBy(container, delta);

    /* --- sync sibling --- */
    const sibling = this.getSiblingSwatchContainer();
    const siblingContainer = sibling?.swatchScrollContainer;
    this.animateScrollBy(siblingContainer, delta);

  }

  updateSwatchNavState() {
    const container = this.swatchScrollContainer;
    if (!container) return;

    const prev = this.querySelector('.swatch-nav--prev');
    const next = this.querySelector('.swatch-nav--next');

    if (!prev || !next) return;

    // Hide buttons if container is not scrollable (centered layout)
    if (!container.classList.contains('is-scrollable')) {
      prev.style.display = 'none';
      next.style.display = 'none';
      return;
    }

    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    const hasOverflow = scrollWidth > clientWidth + 2;

    if (!hasOverflow) {
      prev.disabled = true;
      next.disabled = true;
      prev.style.display = 'none';
      next.style.display = 'none';
      return;
    }

    prev.style.display = '';
    next.style.display = '';
    prev.disabled = scrollLeft <= 2;
    next.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
  }
 
  animateScrollBy(container, delta) {
    const duration = 400;
    const delay = 0;
    const start = container.scrollLeft;
    const target = start + delta;

    const ease = t => 1 - Math.pow(1 - t, 3); // easeOutCubic

    setTimeout(() => {
      const startTime = performance.now();

      const frame = now => {
        const progress = Math.min((now - startTime) / duration, 1);
        container.scrollLeft =
          start + (target - start) * ease(progress);

        if (progress < 1) {
          requestAnimationFrame(frame);
        }
      };

      requestAnimationFrame(frame);
    }, delay);
  }

  // Tooltip helpers
  createTooltip() {
    if (this.tooltip) return;

    const tooltip = document.createElement("div");
    tooltip.className = "swatch-tooltip";
    tooltip.setAttribute("role", "tooltip");

    Object.assign(tooltip.style, {
      position: "absolute",
      pointerEvents: "none",
      opacity: "0",
      transition: "opacity 0.15s ease, transform 0.15s ease",
      transform: "translateY(4px)",
      zIndex: "999",
    });

    document.body.appendChild(tooltip);
    this.tooltip = tooltip;
  }

  showTooltip(target) {
    if (!this.tooltip) this.createTooltip();

    const fullTitle = target.dataset.title;
    if (!fullTitle) return;

    const tooltipText = fullTitle.includes(' - ')
      ? fullTitle.split(' - ').slice(1).join(' - ').trim()
      : fullTitle;
    this.tooltip.textContent = tooltipText;

    const rect = target.getBoundingClientRect();
    const tooltipRect = this.tooltip.getBoundingClientRect();

    const top = rect.top + window.scrollY - tooltipRect.height - 8;

    const left =
      rect.left + window.scrollX + rect.width / 2 - tooltipRect.width / 2;

    Object.assign(this.tooltip.style, {
      top: `${top}px`,
      left: `${left}px`,
      opacity: "1",
      transform: "translateY(0)",
      fontSize: "8px",
      padding: "5px",
      maxWidth: "150px",
      backgroundColor: "rgba(247, 247, 247, 1)",
      textAlign: "center",
      fontWeight: "bold",
    });
  }

  hideTooltip() {
    if (!this.tooltip) return;

    Object.assign(this.tooltip.style, {
      opacity: "0",
      transform: "translateY(4px)",
    });
  }
}

customElements.define("product-swatches", ProductSwatches);

/* PDP swatches carousel: single script for both card and product page */
(function initPdpSwatches() {
  function getSectionId() {
    var el = document.querySelector('.template-product__container[data-section-id]');
    return el ? el.getAttribute('data-section-id') : null;
  }

  function loadProductSection(url, callback) {
    var sid = getSectionId();
    if (!sid) {
      window.location.href = url;
      return;
    }
    var u = new URL(url, window.location.origin);
    u.searchParams.set('sections', sid);
    fetch(u.toString(), { headers: { Accept: 'application/json' } })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        var html = data && data[sid];
        if (!html) {
          window.location.href = url;
          return;
        }
        var wrapper = document.getElementById('shopify-section-' + sid);
        if (wrapper) wrapper.outerHTML = html;
        if (typeof history !== 'undefined' && history.pushState) history.pushState({}, '', url);
        if (typeof callback === 'function') setTimeout(callback, 0);
      })
      .catch(function() { window.location.href = url; });
  }

  function scrollActiveSwatchIntoView(instant) {
    var scrollEl = document.querySelector('[js-pdp-swatches="scroll"]');
    if (!scrollEl) return;

    let newScrollLeft;

    const scrollLeftFromUrl = getSwatchScrollFromUrl();
    if (typeof scrollLeftFromUrl === 'number') {
      newScrollLeft = scrollLeftFromUrl;
    } else {
      var active = scrollEl.querySelector('.product-swatches__link.is-active');
      if (!active) return;
      var activeSwatch = active.querySelector('.product-swatches__swatch');
      var paddingLeft = activeSwatch ? window.getComputedStyle(activeSwatch).paddingLeft : window.getComputedStyle(active).paddingLeft;
      var links = scrollEl.querySelectorAll('.product-swatches__link');
      var firstLink = links[0];
      var itemWidth = firstLink ? firstLink.offsetWidth : 118.25;
      var activeIndex = Array.prototype.indexOf.call(links, active);
      if (activeIndex < 0) activeIndex = 0;
      var paddingLeftPx = parseFloat(paddingLeft) || 0;
      var OFFSET = 4;
      newScrollLeft = (activeIndex * itemWidth) + (paddingLeftPx * 2) - OFFSET;
    }

    if (instant) {
      scrollEl.style.scrollBehavior = 'auto';
    }
    scrollEl.scrollLeft = newScrollLeft;
    if (instant) {
      scrollEl.offsetLeft; // force reflow to apply scroll position before resetting behavior
      scrollEl.style.scrollBehavior = '';
    }

    // Reveal after scroll position is set to prevent flash at scrollLeft 0
    scrollEl.style.visibility = 'visible';

    updateArrowState();
    var container = scrollEl.closest('[js-pdp-swatches="container"]');
    if (container) container.scrollIntoView({ behavior: instant ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
  }

  var PDP_DESKTOP_BREAKPOINT = 1024;

  function updateArrowState() {
    var container = document.querySelector('[js-pdp-swatches="container"]');
    if (!container) return;
    var scrollEl = container.querySelector('[js-pdp-swatches="scroll"]');
    var prev = container.querySelector('[js-pdp-swatches="prev"]');
    var next = container.querySelector('[js-pdp-swatches="next"]');
    var arrowsEl = container.querySelector('[js-pdp-swatches="arrows"]');
    if (!scrollEl || !prev || !next) return;
    var links = scrollEl.querySelectorAll('.product-swatches__link');
    var linkCount = links.length;
    var isDesktop = window.innerWidth >= PDP_DESKTOP_BREAKPOINT;
    if (arrowsEl && isDesktop && linkCount === 4) {
      arrowsEl.style.display = 'none';
      return;
    }
    if (arrowsEl) arrowsEl.style.display = '';
    var atStart = scrollEl.scrollLeft <= 1;
    var firstLink = scrollEl.querySelector('.product-swatches__link');
    var endThreshold = firstLink ? firstLink.offsetWidth : 118;
    var atEnd = scrollEl.scrollLeft + scrollEl.clientWidth >= scrollEl.scrollWidth - endThreshold;
    prev.classList.toggle('is-disabled', atStart);
    next.classList.toggle('is-disabled', atEnd);
  }

  function initArrows() {
    var container = document.querySelector('[js-pdp-swatches="container"]');
    if (!container) return;
    var scrollEl = container.querySelector('[js-pdp-swatches="scroll"]');
    var prev = container.querySelector('[js-pdp-swatches="prev"]');
    var next = container.querySelector('[js-pdp-swatches="next"]');
    if (!scrollEl || !prev || !next) return;
    var firstLink = scrollEl.querySelector('.product-swatches__link');
    var step = firstLink ? firstLink.offsetWidth : 118.25;
    prev.addEventListener('click', function(e) {
      if (prev.classList.contains('is-disabled')) return;
      e.preventDefault();
      scrollEl.scrollLeft -= step;
      setTimeout(updateArrowState, 50);
    });
    next.addEventListener('click', function(e) {
      if (next.classList.contains('is-disabled')) return;
      e.preventDefault();
      scrollEl.scrollLeft += step;
      setTimeout(updateArrowState, 50);
    });
    scrollEl.addEventListener('scroll', updateArrowState);
    window.addEventListener('resize', updateArrowState);
    updateArrowState();
  }

  function trimModelFromTitle(title) {
    if (!title || typeof title !== 'string') return title;
    const idx = title.indexOf(' - ');
    if (idx === -1) return title;

    const remainder = title.slice(idx + 3).trim();
    const parts = remainder.split(' - ');
    return parts.reverse().join(' - ');
  }

  function initSwatchNameHover() {
    var container = document.querySelector('[js-pdp-swatches="container"]');
    if (!container) return;
    var colorNameEl = container.querySelector('[js-pdp-swatches="colorName"]');
    if (!colorNameEl) return;
    function setActiveName() {
      var active = container.querySelector('.product-swatches__link.is-active');
      if (!active) return;
      var swatch = active.querySelector('.product-swatches__swatch');
      var name = swatch && swatch.getAttribute('data-color-name');
      colorNameEl.textContent = name ? trimModelFromTitle(name) : '';
    }
    container.addEventListener('mouseover', function(e) {
      var link = e.target.closest && e.target.closest('[js-pdp-swatches="link"]');
      if (!link) return;
      var swatch = link.querySelector('.product-swatches__swatch');
      var name = swatch && swatch.getAttribute('data-color-name');
      colorNameEl.textContent = name ? trimModelFromTitle(name) : '';
    });
    container.addEventListener('mouseout', function(e) {
      if (container.contains(e.relatedTarget)) return;
      setActiveName();
    });
    setActiveName();
  }

  function init(instant) {
    scrollActiveSwatchIntoView(instant);
    initArrows();
    initSwatchNameHover();
    setTimeout(updateArrowState, 450);
    saveSwatchScrollToUrl();
    appendScrollLeftToLinks();

    const scrollEl = document.querySelector('[js-pdp-swatches="scroll"]');
    if (scrollEl) {
      scrollEl.removeEventListener('scroll', onScrollElementListener);
      scrollEl.addEventListener('scroll', onScrollElementListener);
    }
  }

  function onLinkClick(e) {
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return;
    var link = e.target.closest && e.target.closest('[js-pdp-swatches="link"]');
    if (!link || !link.href) return;
    var path = new URL(link.href, window.location.origin).pathname;
    if (path === window.location.pathname) return;
    e.preventDefault();
    loadProductSection(link.href, init);
  }

  document.addEventListener('click', function(e) {
    if (e.target.closest && e.target.closest('[js-pdp-swatches="container"]')) onLinkClick(e);
  }, false);

  function saveSwatchScrollToUrl() {
    const container = document.querySelector('[js-pdp-swatches="container"]');
    if (!container) return;
    const scrollEl = container.querySelector('[js-pdp-swatches="scroll"]');
    if (!scrollEl) return;

    const scrollLeftValue = Math.floor(scrollEl.scrollLeft);

    // save scroll left to url as query param
    var url = new URL(window.location.href);
    url.searchParams.set(SCROLL_LEFT_SWATCH_QUERY_PARAM, scrollLeftValue);
    window.history.replaceState({}, '', url.toString());
  }

  function appendScrollLeftToLinks() {
    const container = document.querySelector('[js-pdp-swatches="container"]');
    if (!container) return;
    const scrollEl = container.querySelector('[js-pdp-swatches="scroll"]');
    if (!scrollEl) return;

    const scrollLeftValue = Math.floor(scrollEl.scrollLeft);

    const links = container.querySelectorAll('a');
    links.forEach(function(link) {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set(SCROLL_LEFT_SWATCH_QUERY_PARAM, scrollLeftValue);
      link.href = url.toString();
    });
  }

  function getSwatchScrollFromUrl() {
    var url = new URL(window.location.href);
    var scrollLeftValue = parseInt(url.searchParams.get(SCROLL_LEFT_SWATCH_QUERY_PARAM));
    if (isNaN(scrollLeftValue)) return;
    return scrollLeftValue;
  }

  function onScrollElementListener() {
    saveSwatchScrollToUrl();
    appendScrollLeftToLinks();
  }

  function runIfPdpPresent() {
    if (!document.querySelector('[js-pdp-swatches="container"]')) return;

    // Force visibility of scroll element after a delay incase the active swatch is never set
    const scrollEl = document.querySelector('[js-pdp-swatches="scroll"]');
    if (scrollEl) {
      setTimeout(function() {
        scrollEl.style.visibility = 'visible';
      }, 1000);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() { setTimeout(function() { init(true); }, 0); });
    } else {
      setTimeout(function() { init(true); }, 0);
    }
  }

  runIfPdpPresent();
})();
